import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useControls, useFrameworks } from "../../hooks/useControls.js"
import API from '../../services/api.js'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, ChevronDown, Check, Upload, AlertTriangle } from 'lucide-react'

function CoverageStatusBadge({ status }) {
  const styles = {
    mapped: 'bg-green-500/10 text-green-400 border border-green-500/20',
    partial: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
    gap: 'bg-red-500/10 text-red-400 border border-red-500/20',
    not_applicable: 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
  }

  return (
    <span className={`
      px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs
      font-medium inline-flex items-center gap-2
      ${styles[status] || styles.not_applicable}
    `}>
      {status
        ?.replace('_', ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
      }
    </span>
  )
}

function RedteamAlertPill({ severity }) {
  if (!severity) return null
  const styles = {
    high: 'bg-red-500/15 text-red-300 border-red-500/30',
    medium: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
    low: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  }
  return (
    <span className={`
      px-2 py-0.5 rounded-full text-[10px] font-medium
      inline-flex items-center gap-1 border
      ${styles[severity] || styles.low}
    `}>
      <AlertTriangle size={10} />
      Red-team
    </span>
  )
}

/* Upload modal for red-team HTML reports. Backend runs async via
   Celery — upload returns 202 immediately with a run_id, then this
   modal polls RedteamRunStatusView until status is 'done'/'failed'.
   asset_id is derived server-side from the caller's profile, not
   sent from the client. */
function RedteamUploadModal({ assetId, onClose, onUploaded }) {
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [polling, setPolling] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const fileInputRef = useRef(null)

  async function pollRunStatus(runId, assetId) {
    setPolling(true)
    const POLL_INTERVAL_MS = 5000
    const MAX_ATTEMPTS = 360

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
      try {
        const { data } = await API.get(`/controls/redteam-runs/${runId}/`, {
          params: { asset_id: assetId },
        })

        if (data.status === 'done') {
          setResult({ findings_parsed: data.findings_parsed, findings_mapped: data.findings_mapped })
          onUploaded?.()
          setPolling(false)
          return
        }
        if (data.status === 'failed') {
          setError(data.error || 'Processing failed')
          setPolling(false)
          return
        }
        // status === 'processing' — keep polling
      } catch (err) {
        setError(err?.response?.data?.detail || err.message)
        setPolling(false)
        return
      }
    }

    setError('Processing is taking longer than expected — check back later.')
    setPolling(false)
  }

  async function handleSubmit() {
    if (!file) { setError('Please select an HTML report file'); return }
    setSubmitting(true)
    setError(null)
    try {
      const html = await file.text()
      const { data } = await API.post('/controls/redteam-upload/', {
        html,
        filename: file.name,
        asset_id: assetId,
      })
      if (!data?.success) throw new Error(data?.error || 'Upload failed')

      setSubmitting(false)
      await pollRunStatus(data.data.run_id, assetId)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
      setSubmitting(false)
    }
  }

  const busy = submitting || polling

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-0 sm:px-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="
          w-full sm:max-w-lg
          rounded-t-2xl sm:rounded-2xl
          bg-gradient-to-b from-white/10 to-white/5
          backdrop-blur-2xl
          border border-white/10
          shadow-[0_-4px_40px_rgba(0,0,0,0.5)] sm:shadow-[0_10px_40px_rgba(0,0,0,0.6)]
          p-5 sm:p-6
          max-h-[92dvh] overflow-y-auto
        "
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4 sm:hidden" />

        <h2 className="text-base sm:text-xl font-semibold text-white mb-2 tracking-tight">
          Upload Red-team Report
        </h2>
        <p className="text-xs text-gray-400 mb-5 sm:mb-6">
          Upload an HTML scan report. Findings will be parsed and mapped to controls automatically.
        </p>

        {!result ? (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                HTML Report <span className="text-red-400">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".html"
                disabled={busy}
                onChange={(e) => setFile(e.target.files[0])}
                className="
                  w-full px-4 py-3 rounded-lg text-sm
                  bg-white/5 border border-white/10 text-gray-300
                  file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0
                  file:text-xs file:bg-white/10 file:text-gray-300
                  hover:file:bg-white/20
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-all duration-200
                  disabled:opacity-50
                "
              />
              {file && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {polling && (
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                <div className="w-3.5 h-3.5 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
                Parsing and mapping findings — this can take a few minutes for large reports.
              </div>
            )}

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-400 mt-4"
              >
                {error}
              </motion.p>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end items-stretch sm:items-center gap-2 sm:gap-3 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-gray-500/50 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={busy}
                className="px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? 'Uploading…' : polling ? 'Parsing & Mapping…' : 'Upload & Map'}
              </button>
            </div>
          </>
        ) : (
          <div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-300 font-medium mb-1">Report processed</p>
              <p className="text-xs text-gray-400">
                {result.findings_parsed} findings parsed · {result.findings_mapped} mapped to controls
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-all duration-200"
            >
              Done
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

function Header({ navigate, onUploadClick, canUpload }) {
  return (
    <div className="mb-6 sm:mb-10 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <motion.button
          onClick={() => navigate('/grc/framework-review')}
          title="Go back"
          whileHover={{ scale: 1.08, x: -2 }}
          whileTap={{ scale: 0.92 }}
          transition={{ type: 'spring', stiffness: 260, damping: 16 }}
          className="
            flex items-center justify-center
            h-[28px] sm:h-[36px] lg:h-[44px]
            text-gray-400 hover:text-white
            transition-colors duration-200
            flex-shrink-0
          "
        >
          <ArrowLeft className="w-5 h-5" />
        </motion.button>

        <div className="flex flex-col justify-center">
          <h1 className="
            text-lg sm:text-2xl lg:text-3xl
            font-semibold text-white tracking-tight
          ">
            Controls
          </h1>

          <div
            className="
              mt-1 h-[2px]
              w-16 sm:w-32 lg:w-44
              bg-gradient-to-r
              from-blue-500/60
              via-blue-400/10
              to-transparent
              animate-pulse
            "
          />
        </div>
      </div>

      {canUpload && (
        <motion.button
          onClick={onUploadClick}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="
            flex items-center gap-1.5 sm:gap-2
            px-3 sm:px-4 py-1.5 sm:py-2
            text-xs sm:text-sm font-medium rounded-xl
            bg-white/5 border border-white/10 text-gray-200
            hover:bg-white/10 focus:outline-none
            transition-colors duration-200 whitespace-nowrap
          "
        >
          <Upload size={14} />
          <span className="hidden sm:inline">Upload Red-team Report</span>
          <span className="sm:hidden">Upload</span>
        </motion.button>
      )}
    </div>
  )
}

function FrameworkDropdown({ frameworks, selected, onSelect, open, onToggle, dropdownRef }) {
  const selectedName = selected
    ? frameworks.find(f => f.id === selected)?.name
    : 'All Frameworks'

  return (
    <div className="relative w-full sm:w-64" ref={dropdownRef}>
      <button
        type="button"
        onClick={onToggle}
        className={`
          w-full flex items-center justify-between gap-2
          px-3.5 py-2.5 rounded-xl
          text-sm text-left
          border transition-all duration-200
          ${open
            ? 'bg-white/[0.07] border-white/20 text-white'
            : 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/[0.07] hover:border-white/15'
          }
        `}
      >
        <span className="truncate">{selectedName}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-gray-300' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="
              absolute z-50 mt-2 w-full
              bg-[#0c0c0f] border border-white/10
              rounded-xl
              max-h-64 overflow-y-auto
              shadow-2xl shadow-black/50
              py-1.5
            "
          >
            <DropdownOption
              label="All Frameworks"
              isSelected={!selected}
              onClick={() => onSelect('')}
            />
            {frameworks.map(f => (
              <DropdownOption
                key={f.id}
                label={f.name}
                isSelected={selected === f.id}
                onClick={() => onSelect(f.id)}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DropdownOption({ label, isSelected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full flex items-center justify-between gap-2
        px-3.5 py-2.5 text-sm text-left
        transition-colors duration-150
        ${isSelected ? 'text-white bg-white/[0.06]' : 'text-gray-300 hover:bg-white/5 hover:text-white'}
      `}
    >
      <span className="truncate">{label}</span>
      {isSelected && <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
    </button>
  )
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'mapped', label: 'Mapped' },
  { key: 'partial', label: 'Partial' },
  { key: 'gap', label: 'Gap' },
  { key: 'alerts', label: 'Alerts' },
]

function StatusFilterSegment({ active, onChange, counts, total }) {
  return (
    <div
      className="
        inline-flex items-center gap-0.5
        p-1 rounded-xl
        bg-white/[0.03] border border-white/10
        shrink-0
        overflow-x-auto
      "
    >
      {STATUS_FILTERS.map(({ key, label }) => {
        const isActive = active === key
        const count = key === 'all' ? total : counts[key]
        const isAlerts = key === 'alerts'
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`
              relative px-3 sm:px-3.5 py-1.5 rounded-lg
              text-xs sm:text-sm font-medium
              whitespace-nowrap
              transition-all duration-200
              flex items-center gap-1.5
              ${isActive
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
              }
            `}
          >
            {isAlerts && (
              <AlertTriangle
                size={12}
                className={isActive ? 'text-red-600' : 'text-red-400'}
              />
            )}
            {label} {count}
          </button>
        )
      })}
    </div>
  )
}

export default function Controls() {
  const navigate = useNavigate()

  // Page-level asset scope — same sessionStorage → /resolve-asset-id/
  // pattern already used by Policies.jsx and RCMBuilder.jsx. Controls
  // and their coverage_status are meaningless without an asset, since
  // org_control_status (where mapped/partial/gap actually lives) is
  // keyed on (tenant_id, asset_id, control_id) — see controls/models.py.
  const selectedAppName = sessionStorage.getItem('selectedApp')
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [assetError, setAssetError] = useState(null)

  useEffect(() => {
    if (!selectedAppName) {
      setAssetError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => {
        setAssetId(data.id)
        setAssetError(null)
      })
      .catch(() => {
        setAssetError('Could not resolve the selected asset. Please re-select it from the dashboard.')
      })
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  const { frameworks, loading: frameworksLoading } = useFrameworks()
  const [selectedFramework, setSelectedFramework] = useState('')
  const { controls, loading: controlsLoading, error, refetch } =
    useControls(assetId, selectedFramework || null)

  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)

  useEffect(() => {
    async function fetchUser() {
      try {
        // MeView doesn't require an asset (require_asset=False server-side)
        const { data } = await API.get('/auth/me/')
        setCurrentUser(data)
      } catch (err) {
        console.error('Failed to fetch current user:', err?.response?.data?.detail || err.message)
      }
    }
    fetchUser()
  }, [])

  const [statusFilter, setStatusFilter] = useState('all')

  const pageLoading = resolvingAsset || frameworksLoading || controlsLoading

  const allControls = controls || []

  const counts = {
    mapped: allControls.filter(c => c.coverage_status === 'mapped').length,
    partial: allControls.filter(c => c.coverage_status === 'partial').length,
    gap: allControls.filter(c => c.coverage_status === 'gap').length,
    not_applicable: allControls.filter(c => c.coverage_status === 'not_applicable').length,
    alerts: allControls.filter(c => c.redteam_alert).length,
  }

  const safeControls = statusFilter === 'all'
    ? allControls
    : statusFilter === 'alerts'
      ? allControls.filter(c => c.redteam_alert)
      : allControls.filter(c => c.coverage_status === statusFilter)

  const frameworksById = Object.fromEntries(frameworks.map(f => [f.id, f]))

  const showFrameworkTag = !selectedFramework

  const redteamAlertCount = counts.alerts

  return (
    <div className="relative min-h-screen overflow-hidden">

      {pageLoading && (
        <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Loading...</p>
          </div>
        </div>
      )}

      {showUploadModal && (
        <RedteamUploadModal
          assetId={assetId}
          onClose={() => setShowUploadModal(false)}
          onUploaded={refetch}
        />
      )}

      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">

        <Header
          navigate={navigate}
          onUploadClick={() => setShowUploadModal(true)}
          canUpload={currentUser?.role !== 'viewer'}
        />

        {assetError && (
          <div className="mb-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{assetError}</p>
          </div>
        )}

        {redteamAlertCount > 0 && (
          <motion.button
            type="button"
            onClick={() => setStatusFilter('alerts')}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="
              w-full mb-4 sm:mb-5 flex items-center gap-2 px-4 py-3 rounded-xl
              bg-red-500/10 border border-red-500/20
              hover:bg-red-500/15 transition-colors duration-200
              text-left
            "
          >
            <AlertTriangle size={16} className="text-red-400 shrink-0" />
            <p className="text-xs sm:text-sm text-red-300">
              <span className="font-semibold">{redteamAlertCount}</span> control{redteamAlertCount === 1 ? '' : 's'} have red-team findings requiring attention
            </p>
          </motion.button>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-5 sm:mb-6">
          {[
            { label: 'Mapped', value: counts.mapped, style: 'text-green-400 bg-green-500/10 border-green-500/20' },
            { label: 'Partial', value: counts.partial, style: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
            { label: 'Gap', value: counts.gap, style: 'text-red-400 bg-red-500/10 border-red-500/20' },
            { label: 'N/A', value: counts.not_applicable, style: 'text-gray-300 bg-gray-500/10 border-gray-500/20' }
          ].map(item => (
            <div
              key={item.label}
              className={`rounded-xl p-3 sm:p-4 border ${item.style}`}
            >
              <p className="text-xl sm:text-2xl font-semibold">
                {item.value}
              </p>
              <p className="text-[11px] sm:text-xs mt-1 text-gray-300">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">

          <FrameworkDropdown
            frameworks={frameworks}
            selected={selectedFramework}
            onSelect={(id) => { setSelectedFramework(id); setOpen(false) }}
            open={open}
            onToggle={() => setOpen(prev => !prev)}
            dropdownRef={dropdownRef}
          />

          <StatusFilterSegment
            active={statusFilter}
            onChange={setStatusFilter}
            counts={counts}
            total={allControls.length}
          />
        </div>

        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl overflow-hidden">

          {error ? (
            <div className="p-6 text-red-400 text-sm">{error}</div>
          ) : !safeControls.length && !pageLoading ? (
            <div className="text-center py-14 text-gray-500">
              {statusFilter === 'all'
                ? 'No controls found'
                : statusFilter === 'alerts'
                  ? 'No controls with open red-team alerts'
                  : `No ${statusFilter} controls found`}
            </div>
          ) : (
            <div className="divide-y divide-white/5">

              {safeControls.map(control => (
                <div
                  key={control.id}
                  className="
                    group
                    px-4 sm:px-5 py-4
                    transition
                    active:bg-white/5
                    sm:hover:bg-white/5
                  "
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">

                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm sm:text-base font-medium truncate">
                        {control.title}
                      </p>

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {showFrameworkTag && (
                          <span className="
                            text-[10px] sm:text-[11px] font-medium
                            text-blue-300 bg-blue-500/10 border border-blue-500/20
                            px-1.5 py-0.5 rounded-md
                          ">
                            {control.frameworks?.name ?? frameworksById[control.framework_id]?.name ?? 'Unknown framework'}
                          </span>
                        )}
                        <span className="text-[11px] sm:text-xs text-gray-400 uppercase">
                          {control.category}
                        </span>

                        <span className="text-[11px] sm:text-xs text-gray-600">
                          {control.control_id}
                        </span>
                      </div>
                    </div>

                    <div className="
                      flex items-center justify-between sm:justify-end
                      gap-3 mt-2 sm:mt-0
                    ">
                      <div className="flex items-center gap-2">
                        <CoverageStatusBadge status={control.coverage_status} />
                        <RedteamAlertPill severity={control.redteam_alert_severity} />
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/grc/controls/${control.id}`)
                        }}
                        className="
                          px-3 py-1.5
                          text-xs
                          bg-white/5 border border-white/10
                          rounded-md
                          text-gray-300
                          hover:text-white
                          hover:border-gray-500/50
                          hover:bg-white/10
                          whitespace-nowrap
                        "
                      >
                        View
                      </motion.button>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          )}
        </div>
      </div>
    </div>
  )
}